/* TanStack Query hooks over the API client — the single place server state is
 * fetched/cached/invalidated. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SignupInput, LoginInput, CreateClassInput, JoinClassInput,
  CreateTeamInput, CreateGameInput, SubmitRoundInput, UpdatePreferencesInput,
  SetGradeInput, UpdateClassInput, SubmitQuizInput, SubmitJournalInput,
} from '@venturewrist/shared';
import { api, ApiError } from '../lib/api';
import { useTutorial } from '../tutorial/useTutorial';
import type { PublicUser } from '../lib/types';

export function useAuth() {
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<PublicUser | null> => {
      try {
        return (await api.me()).user;
      } catch (e) {
        // A clean 401 means "logged out". Any other error (API restart, network
        // blip) is transient — rethrow so it does NOT get treated as logged out.
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    // Auth state only changes via explicit login/logout (handled by setQueryData),
    // so never auto-refetch it — that background refetch was what briefly nulled
    // the user and bounced players out of their game.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: (count, e) => !(e instanceof ApiError && e.status === 401) && count < 2,
  });
  return { user: q.data ?? null, isLoading: q.isPending, status: q.status, isError: q.isError };
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: SignupInput) => api.signup(b),
    onSuccess: (r) => qc.setQueryData(['me'], r.user),
  });
}
export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: LoginInput) => api.login(b),
    onSuccess: (r) => qc.setQueryData(['me'], r.user),
  });
}
export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.logout(),
    // Tutorial progress is per-user session state — reset it with the caches.
    onSuccess: () => { qc.setQueryData(['me'], null); qc.clear(); useTutorial.getState().reset(); },
  });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: UpdatePreferencesInput) => api.updatePreferences(b),
    // Optimistic: reflect the toggle immediately, roll back on error.
    onMutate: async (b) => {
      const prev = qc.getQueryData<PublicUser | null>(['me']);
      if (prev) qc.setQueryData<PublicUser>(['me'], { ...prev, ...b });
      return { prev };
    },
    onError: (_e, _b, ctx) => {
      if (ctx && 'prev' in ctx) qc.setQueryData(['me'], ctx.prev);
    },
    onSuccess: (r) => qc.setQueryData(['me'], r.user),
  });
}

export const useClasses = () =>
  useQuery({ queryKey: ['classes'], queryFn: () => api.listClasses().then((r) => r.classes) });

export const useTeams = (classId: string | undefined) =>
  useQuery({
    queryKey: ['teams', classId],
    queryFn: () => api.listTeams(classId!).then((r) => r.teams),
    enabled: !!classId,
  });

export const useTeam = (teamId: string | undefined) =>
  useQuery({
    queryKey: ['team', teamId],
    queryFn: () => api.getTeam(teamId!),
    enabled: !!teamId,
  });

export const useGame = (gameId: string | undefined) =>
  useQuery({
    queryKey: ['game', gameId],
    queryFn: () => api.getGame(gameId!),
    enabled: !!gameId,
  });

export function useCreateClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: CreateClassInput) => api.createClass(b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['classes'] }),
  });
}
export function useJoinClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: JoinClassInput) => api.joinClass(b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['classes'] }),
  });
}
export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: CreateTeamInput) => api.createTeam(b),
    onSuccess: (_r, vars) => qc.invalidateQueries({ queryKey: ['teams', vars.classId] }),
  });
}
export function useJoinTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: string) => api.joinTeam(teamId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  });
}
export function useCreateGame() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: CreateGameInput) => api.createGame(b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  });
}
export function useAdvance(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: SubmitRoundInput) => api.advanceGame(gameId, b),
    // Refetch on failure too: a 409 means a teammate already resolved this
    // quarter (or the game finished), so the page should move to the new round.
    onSettled: () => qc.invalidateQueries({ queryKey: ['game', gameId] }),
  });
}

// ── Assessment (Phase 3 #2) ──────────────────────────────────────────────────

export function useResetStudentPassword(classId: string | undefined) {
  return useMutation({
    mutationFn: (userId: string) => api.resetStudentPassword(classId!, userId),
  });
}

export const useStudentReview = (classId: string | undefined, userId: string | undefined) =>
  useQuery({
    queryKey: ['review', classId, userId],
    queryFn: () => api.reviewStudent(classId!, userId!),
    enabled: !!classId && !!userId,
  });

export function useSubmitQuiz(classId: string | undefined) {
  return useMutation({
    mutationFn: (b: SubmitQuizInput) => api.submitQuiz(classId!, b),
  });
}

export const useMyJournals = (classId: string | undefined) =>
  useQuery({
    queryKey: ['journals', classId],
    queryFn: () => api.myJournals(classId!).then((r) => r.journals),
    enabled: !!classId,
  });

export function useSubmitJournal(classId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: SubmitJournalInput) => api.submitJournal(classId!, b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journals', classId] }),
  });
}

export const useClassOverview = (id: string | undefined) =>
  useQuery({ queryKey: ['overview', id], queryFn: () => api.classOverview(id!), enabled: !!id });

export function useSetGrade(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { userId: string; body: SetGradeInput }) => api.setGrade(classId, v.userId, v.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['overview', classId] }),
  });
}
export function useUpdateClass(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: UpdateClassInput) => api.updateClass(classId, b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['overview', classId] }); qc.invalidateQueries({ queryKey: ['classes'] }); },
  });
}
